import { motion } from "framer-motion";
import { Phone, ArrowUpRight, Sparkles } from "lucide-react";
import { BRAND, PLATFORMS, scrollToId } from "./data";
import { useState } from "react";

const scrollTo = (id) => scrollToId(id);

const HEADLINE = ["We architect", "intelligent apps", "for every platform."];

export const Hero = () => {
  const [active, setActive] = useState("iOS");

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center pt-32 pb-20 px-5 overflow-hidden grain"
    >
      <div className="absolute inset-0 ambient-glow" />
      <div className="absolute inset-0 circuit-grid opacity-60" />
      <div className="absolute top-24 -left-20 w-80 h-80 rounded-full bg-indigo-600/20 blur-[100px] animate-float" />
      <div className="absolute bottom-10 right-0 w-96 h-96 rounded-full bg-purple-600/20 blur-[120px] animate-float" style={{ animationDelay: "1.5s" }} />

      <div className="relative max-w-7xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8"
          data-testid="hero-status-pill"
        >
          <span className="relative flex w-2.5 h-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 pulse-dot" />
            <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-emerald-400" />
          </span>
          <span className="font-mono text-[11px] tracking-[0.2em] uppercase txt-soft">
            Accepting new builds · 100% Canadian craft
          </span>
        </motion.div>

        <h1 data-testid="hero-headline" className="font-heading font-extrabold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl max-w-5xl">
          {HEADLINE.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className={`block ${i === 1 ? "text-gradient" : "txt-primary"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-7 max-w-xl text-lg txt-soft leading-relaxed"
        >
          Brainstem builds high-octane mobile, web, and cloud software for
          founders and businesses across Canada — turning complex ideas into
          effortless digital velocity.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.85 }}
          className="mt-8 flex flex-wrap gap-2"
        >
          {PLATFORMS.map((p) => (
            <button
              key={p}
              data-testid={`hero-platform-${p.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setActive(p)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                active === p
                  ? "border-purple-400/60 bg-purple-500/15 txt-primary shadow-[0_0_20px_rgba(168,85,247,0.25)]"
                  : "chip hover:border-purple-400/40"
              }`}
            >
              {p}
            </button>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-9 flex flex-col sm:flex-row gap-3"
        >
          <button
            data-testid="hero-cta-start-project"
            onClick={() => scrollTo("contact")}
            className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-semibold text-white bg-gradient-to-r from-sky-500 via-indigo-600 to-purple-600 hover:shadow-[0_0_40px_rgba(99,102,241,0.55)] transition-shadow"
          >
            <Sparkles className="w-4 h-4" />
            Start your build
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
          <a
            data-testid="hero-cta-call-charles"
            href={`tel:${BRAND.phoneTel}`}
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-semibold txt-primary glass hover:border-purple-400/40 transition-colors"
          >
            <Phone className="w-4 h-4" />
            {BRAND.phoneDisplay}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.15 }}
          className="mt-16 grid grid-cols-3 gap-4 max-w-xl"
          data-testid="hero-stats"
        >
          {[
            { k: "All", v: "Platforms" },
            { k: "100%", v: "On-time delivery" },
            { k: "24h", v: "Response time" },
          ].map((s) => (
            <div key={s.v} className="border-l border-indigo-500/30 pl-4">
              <p className="font-heading text-3xl font-bold txt-primary">{s.k}</p>
              <p className="text-xs txt-dim mt-1">{s.v}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
